import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CardBody, CardTitle, CardSubtitle, Button, Alert, Spinner } from 'reactstrap';
import { UserContext } from '../UserContext';
import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import PremiumQuiz from './PremiumQuiz';
import { getGradeLevel } from '../../utils/gradeUtils';

const UppercaseChallenge = ({ match }) => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const history = useHistory();
  const [challengeCompleted, setChallengeCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  useEffect(() => {
    const loadChallenge = async () => {
      try {
        const grade = await getGradeLevel(user);
        const year = new Date().getFullYear();
        
        const gradeLevel = grade >= 1 && grade <= 5 ? 'Elementary' : (grade >= 6 && grade <= 8 ? 'Junior' : 'Senior');
        const fileName = `Elementary Word List ${year}.json with upper cases.json`;
        const relativePath = `./Grade${grade}/${year}/premium0/${fileName}`;

        console.log('Attempting to load:', relativePath);

        // Use require.context to handle dynamic paths for webpack
        const requireContext = require.context('../practice/resources/SpellBowl', true, /\.json$/);
        let wordList = [];

        try {
          const module = requireContext(relativePath);
          wordList = module.questions || [];
        } catch (err) {
          console.error(`Failed to load module at ${relativePath}`, err);
          throw new Error('Failed to load the uppercase word list.');
        }

        if (wordList.length === 0) {
          throw new Error('Word list is empty or could not be loaded.');
        }
        
        console.log(`Loaded ${wordList.length} uppercase words`);
        
        // Shuffle and select 20 words
        const shuffled = [...wordList].sort(() => 0.5 - Math.random());
        const selectedWords = shuffled.slice(0, 20);
        
        // Format questions for the quiz component
        const formattedQuestions = selectedWords.map((word, index) => ({
          id: index + 1,
          question: word.question,
          correctanswer: word.question, // The word itself is the correct answer
          answers: [word.question], // For compatibility with quiz component
          displayformat: 'SpeakTextwithTextField',
          audio: word.audio,
          meaning: word.meaning,
          example: word.example
        }));

        setQuestions(formattedQuestions);
        setTotalQuestions(formattedQuestions.length);
        setLoading(false);
      } catch (err) {
        console.error('Error loading uppercase challenge:', err);
        setError('Failed to load the challenge. Please try again later.');
        setLoading(false);
      }
    };

    loadChallenge();
  }, [user]);

  const handleComplete = (finalScore) => {
    setScore(finalScore);
    setChallengeCompleted(true);
    
    // Record the challenge completion
    recordChallengeCompletion(finalScore);
  };

  const recordChallengeCompletion = async (finalScore) => {
    try {
      await API.post('quizwizardapi', '/quiz/updatepracticeactivity', {
        body: {
          username: user.username,
          category: 'UppercaseChallenge',
          score: finalScore,
          totalquestions: totalQuestions,
          eventid: `uc-${uuidv4()}`,
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      console.error('Error recording challenge completion:', err);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading Uppercase Challenge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Alert color="danger">{error}</Alert>
        <Button color="primary" onClick={() => window.location.reload()} className="mt-3">
          Try Again
        </Button>
      </div>
    );
  }

  if (challengeCompleted) {
    return (
      <div className="container mt-5 text-center">
        <Card className="shadow">
          <CardBody>
            <h2 className="text-success">Challenge Complete!</h2>
            <h4>Your Score: {score} / {totalQuestions}</h4>
            <p className="text-muted">
              {score === totalQuestions 
                ? 'Perfect! You aced the Uppercase Challenge! 🎉' 
                : score > totalQuestions / 2 
                  ? 'Great job! You did well on the Uppercase Challenge!' 
                  : 'Keep practicing! You\'ll get better with more practice.'}
            </p>
            <div className="mt-4">
              <Button color="primary" onClick={() => window.location.reload()} className="mr-3">
                Try Again
              </Button>
              <Button color="secondary" onClick={() => history.push('/premium')}>
                Back to Premium
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Uppercase Challenge</h2>
      <p className="text-muted mb-4">
        Listen to each word and type it exactly as you hear it, paying attention to uppercase letters.
      </p>
      
      <PremiumQuiz
        questions={questions}
        onComplete={handleComplete}
        challengeType="UPPERCASE"
        showTimer={true}
        timePerQuestion={30}
      />
    </div>
  );
};

export default UppercaseChallenge;
